import React from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Clock, ArrowRight, ShieldCheck, Users, BuildingLibrary } from 'lucide-react';

const contactHighlights = [
  {
    icon: <MapPin className="w-6 h-6 text-cyan-400" />,
    title: 'Oficina central',
    value: 'Av. Circunvalación Agustín Yáñez No. 2343, Col. Moderna, Guadalajara, Jalisco'
  },
  {
    icon: <Phone className="w-6 h-6 text-cyan-400" />,
    title: 'Teléfono',
    value: '(33) 3030 8250 · Ext. 56264'
  },
  {
    icon: <Mail className="w-6 h-6 text-cyan-400" />,
    title: 'Correo',
    value: 'ordenamientoecologico.semadet@jalisco.gob.mx',
    link: 'mailto:ordenamientoecologico.semadet@jalisco.gob.mx'
  },
  {
    icon: <Clock className="w-6 h-6 text-cyan-400" />,
    title: 'Horario',
    value: 'Lunes a Viernes · 9:00 - 17:00 hrs'
  }
];

const directorCards = [
  {
    name: 'Patricia Fregoso Cruz',
    role: 'Directora General de Ordenamiento Territorial',
    phone: '33 3030-8250',
    extension: 'Ext. 56264',
    email: 'patricia.fregoso@jalisco.gob.mx',
    accent: 'from-cyan-500 to-sky-600'
  },
  {
    name: 'Lucía Hidalgo Luna',
    role: 'Directora de Ordenamiento Ecológico y Territorial',
    phone: '33 3030-8250',
    extension: 'Ext. 55721',
    email: 'lucia.hidalgo@jalisco.gob.mx',
    accent: 'from-violet-500 to-fuchsia-600'
  },
  {
    name: 'Oscar Morales Quiroz',
    role: 'Director de Planeación Urbana',
    phone: '33 3030-8250',
    extension: 'Ext. 56254',
    email: 'oscar.moralesquiroz@jalisco.gob.mx',
    accent: 'from-emerald-500 to-teal-600'
  },
  {
    name: 'Luz Ileana Jiménez Pineda',
    role: 'Directora de Gestión Territorial',
    phone: '33 3030-8250',
    extension: 'Ext. 55749',
    email: 'luz.pineda@jalisco.gob.mx',
    accent: 'from-amber-500 to-orange-500'
  }
];

function Contacto() {
  return (
    <div className="min-h-screen bg-slate-950 text-white overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(56,189,248,0.25),_transparent_30%),radial-gradient(circle_at_bottom_right,_rgba(16,185,129,0.22),_transparent_30%)] pointer-events-none" />
      <div className="relative">
        <motion.section
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative pt-24 pb-16 lg:pb-24"
        >
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid gap-10 lg:grid-cols-[1.3fr_0.9fr] items-center">
              <div className="space-y-8">
                <div className="inline-flex items-center gap-3 rounded-full border border-cyan-400/30 bg-cyan-400/10 px-4 py-2 text-xs uppercase tracking-[0.3em] text-cyan-200 shadow-lg shadow-cyan-500/10">
                  <ShieldCheck className="w-4 h-4" /> Atención especializada
                </div>
                <h1 className="text-5xl md:text-6xl font-black tracking-tight text-white leading-tight">
                  Página de <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 to-emerald-400">Contacto</span> SEMADET
                </h1>
                <p className="max-w-2xl text-lg text-slate-300 leading-relaxed">
                  Encuentra los canales oficiales, los responsables de área y la información más relevante para comunicarte con la Dirección de Ordenamiento Ecológico. Una experiencia moderna, clara y 100% enfocada en lo que necesitas.
                </p>
                <div className="grid gap-4 sm:grid-cols-2">
                  <a
                    href="mailto:ordenamientoecologico.semadet@jalisco.gob.mx"
                    className="group inline-flex items-center justify-between rounded-3xl border border-cyan-500/20 bg-white/5 px-6 py-4 text-white transition hover:border-cyan-300 hover:bg-cyan-500/10"
                  >
                    <span className="text-sm font-semibold">Enviar correo</span>
                    <ArrowRight className="w-5 h-5 transition group-hover:translate-x-1" />
                  </a>
                  <a
                    href="tel:+523330308250"
                    className="group inline-flex items-center justify-between rounded-3xl border border-slate-700 bg-slate-900/80 px-6 py-4 text-white transition hover:border-emerald-400 hover:bg-slate-800"
                  >
                    <span className="text-sm font-semibold">Llamar ahora</span>
                    <Phone className="w-5 h-5" />
                  </a>
                </div>
              </div>

              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.15 }}
                className="rounded-[2rem] border border-white/10 bg-white/5 p-8 shadow-2xl shadow-cyan-500/10 backdrop-blur-xl"
              >
                <div className="grid gap-4">
                  {contactHighlights.map((item) => (
                    <div key={item.title} className="rounded-3xl border border-white/10 bg-slate-950/70 p-6 shadow-[0_15px_45px_-20px_rgba(15,23,42,0.7)] transition-transform duration-300 hover:-translate-y-1">
                      <div className="inline-flex h-12 w-12 items-center justify-center rounded-3xl bg-cyan-500/10 text-cyan-300">
                        {item.icon}
                      </div>
                      <p className="mt-6 text-sm uppercase tracking-[0.25em] text-slate-400">{item.title}</p>
                      {item.link ? (
                        <a href={item.link} className="mt-3 block text-xl font-semibold text-white hover:text-cyan-300 transition">
                          {item.value}
                        </a>
                      ) : (
                        <p className="mt-3 text-xl font-semibold text-white leading-snug">{item.value}</p>
                      )}
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </motion.section>

        <section className="relative py-16 lg:py-24">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between mb-12">
              <div>
                <p className="text-sm uppercase tracking-[0.35em] text-cyan-300 font-bold">Contactos directos</p>
                <h2 className="mt-4 text-4xl font-black text-white lg:text-5xl">Equipo por área y atención rápida</h2>
              </div>
              <p className="max-w-2xl text-slate-400">
                Habla directamente con el equipo responsable de ordenamiento, planeación urbana y gestión territorial para recibir atención prioritaria.
              </p>
            </div>

            <div className="grid gap-6 xl:grid-cols-4 lg:grid-cols-2">
              {directorCards.map((director, index) => (
                <motion.article
                  key={director.email}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.55, delay: index * 0.08 }}
                  className="overflow-hidden rounded-[2rem] border border-white/10 bg-slate-900/90 p-6 shadow-[0_30px_80px_-40px_rgba(14,165,233,0.35)] transition hover:-translate-y-1"
                >
                  <div className={`inline-flex rounded-full bg-gradient-to-r ${director.accent} px-4 py-2 text-xs font-black uppercase tracking-[0.3em] text-white shadow-sm shadow-slate-900/20`}>
                    Contacto clave
                  </div>
                  <div className="mt-6 space-y-4">
                    <div>
                      <p className="text-sm text-slate-400 uppercase tracking-[0.25em]">Nombre</p>
                      <h3 className="mt-2 text-2xl font-black text-white leading-tight">{director.name}</h3>
                    </div>
                    <div>
                      <p className="text-sm text-slate-400 uppercase tracking-[0.25em]">Área</p>
                      <p className="mt-2 text-base font-semibold text-slate-200">{director.role}</p>
                    </div>
                    <div className="space-y-3 text-sm text-slate-300">
                      <div className="flex items-center gap-3">
                        <Phone className="w-4 h-4 text-cyan-300" />
                        <span>{director.phone}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <MapPin className="w-4 h-4 text-cyan-300" />
                        <span>{director.extension}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Mail className="w-4 h-4 text-cyan-300" />
                        <a href={`mailto:${director.email}`} className="text-cyan-200 hover:text-cyan-100 transition break-all">
                          {director.email}
                        </a>
                      </div>
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          </div>
        </section>
      </div>

      <footer className="border-t border-white/10 bg-slate-950/90 py-10 text-slate-400">
        <div className="max-w-7xl mx-auto px-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-sm text-white font-semibold">SEMADET · Dirección de Ordenamiento Ecológico</p>
            <p className="text-sm text-slate-400">Gobierno de Jalisco · <span className="text-cyan-300">semadet.jalisco.gob.mx</span></p>
          </div>
          <div className="space-x-4 text-sm">
            <a href="mailto:ordenamientoecologico.semadet@jalisco.gob.mx" className="text-cyan-300 hover:text-white transition">Correo institucional</a>
            <a href="tel:+523330308250" className="text-cyan-300 hover:text-white transition">Teléfono</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Contacto;

